import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  CashCountStyles,
  DENOMINATIONS,
  formatDate,
  grossOf,
  money,
  num,
} from "./CashCountShared";

const View = () => {
  const { id } = useParams();
  const [cashCountData, setCashCountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCashCountData = async () => {
      try {
        setLoading(true);
        const response = await Http.get(`/cash-count/${id}`);
        setCashCountData(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch cash count details");
        console.error("Error fetching cash count:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCashCountData();
  }, [id]);

  const record = cashCountData || {};
  const gross = useMemo(() => grossOf(record), [cashCountData]);
  const pieces = useMemo(
    () => DENOMINATIONS.reduce((sum, d) => sum + num(record[d.qty]), 0),
    [cashCountData]
  );
  const suspense = num(record.suspense);

  const shell = (children) => (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <CashCountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Cash Count Details</h5>
            <p className="text-muted mb-0 tx-12">
              {loading || error || !cashCountData
                ? "Petty cash count record"
                : `Count taken on ${formatDate(record.date)}`}
            </p>
          </div>
          <div className="d-flex gap-2 cc-no-print">
            <Link to="/cash-count" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
            {cashCountData && (
              <>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => window.print()}
                >
                  <i className="fa fa-print mg-r-6"></i>Print
                </button>
                <Link
                  to={`/edit-cash-count/${id}`}
                  className="btn btn-primary"
                >
                  <i className="fa fa-pencil mg-r-6"></i>Edit
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="am-pagebody">{children}</div>
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="cc-panel">
        <div className="cc-empty">
          <ProgressSpinner
            style={{ width: "42px", height: "42px" }}
            strokeWidth="6"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return shell(
      <div className="cc-panel">
        <div className="cc-empty">
          <i className="fa fa-exclamation-triangle"></i>
          <h6 style={{ color: "#e42320" }}>{error}</h6>
          <p className="mb-0 tx-12">Please try again in a moment.</p>
        </div>
      </div>
    );
  }

  if (!cashCountData) {
    return shell(
      <div className="cc-panel">
        <div className="cc-empty">
          <i className="fa fa-folder-open-o"></i>
          <h6>No cash count found</h6>
          <p className="mb-0 tx-12">
            Record #{id} does not exist or has been deleted.
          </p>
        </div>
      </div>
    );
  }

  return shell(
    <>
      <div className="row mg-b-20">
        <div className="col-md-3 mg-b-10">
          <div className="cc-stat">
            <i className="fa fa-calendar-o cc-stat-icon"></i>
            <span className="cc-stat-label">Count Date</span>
            <span className="cc-stat-value">{formatDate(record.date)}</span>
          </div>
        </div>
        <div className="col-md-3 mg-b-10">
          <div className="cc-stat">
            <i className="fa fa-money cc-stat-icon"></i>
            <span className="cc-stat-label">Gross Cash</span>
            <span className="cc-stat-value">৳ {money(gross)}</span>
          </div>
        </div>
        <div className="col-md-3 mg-b-10">
          <div className="cc-stat is-alert">
            <i className="fa fa-exclamation-triangle cc-stat-icon"></i>
            <span className="cc-stat-label">Suspense</span>
            <span className="cc-stat-value">৳ {money(suspense)}</span>
          </div>
        </div>
        <div className="col-md-3 mg-b-10">
          <div className="cc-stat">
            <i className="fa fa-check-circle-o cc-stat-icon"></i>
            <span className="cc-stat-label">Net Total</span>
            <span className="cc-stat-value">
              ৳ {money(record.total_amount)}
            </span>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-4">
          <div className="cc-panel">
            <div className="cc-panel-head">
              <span>
                <i className="fa fa-info-circle me-2"></i>Record Information
              </span>
            </div>
            <div className="cc-panel-body pd-t-6">
              <div className="cc-kv">
                <span className="cc-kv-label">Record ID</span>
                <span className="cc-kv-value">#{record.id}</span>
              </div>
              <div className="cc-kv">
                <span className="cc-kv-label">Count Date</span>
                <span className="cc-kv-value">{formatDate(record.date)}</span>
              </div>
              <div className="cc-kv">
                <span className="cc-kv-label">Notes &amp; Coins</span>
                <span className="cc-kv-value">{pieces}</span>
              </div>
              <div className="cc-kv">
                <span className="cc-kv-label">Created</span>
                <span className="cc-kv-value">
                  {formatDate(record.created_at)}
                </span>
              </div>
              <div className="cc-kv">
                <span className="cc-kv-label">Last Updated</span>
                <span className="cc-kv-value">
                  {formatDate(record.updated_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="cc-panel">
            <div className="cc-panel-head">
              <span>
                <i className="fa fa-calculator me-2"></i>Summary
              </span>
            </div>
            <div className="cc-panel-body">
              <div className="cc-total-box">
                <div className="cc-total-row">
                  <span>Gross cash counted</span>
                  <span>৳ {money(gross)}</span>
                </div>
                <div className="cc-total-row">
                  <span>Less: suspense</span>
                  <span>৳ {money(suspense)}</span>
                </div>
                <div className="cc-total-row cc-total-net">
                  <span>Net Total</span>
                  <span>৳ {money(record.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="cc-panel">
            <div className="cc-panel-head">
              <span>
                <i className="fa fa-money me-2"></i>Denomination Breakdown
              </span>
              <span className="cc-head-note">
                {pieces} pieces across {DENOMINATIONS.length} denominations
              </span>
            </div>
            <div className="table-responsive">
              <table className="table cc-count-table">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Denomination</th>
                    <th className="cc-num" style={{ width: "20%" }}>
                      Quantity
                    </th>
                    <th className="cc-num" style={{ width: "25%" }}>
                      Face Value
                    </th>
                    <th className="cc-num" style={{ width: "25%" }}>
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {DENOMINATIONS.map((d) => {
                    const quantity = num(record[d.qty]);
                    const lineTotal = quantity * d.value;
                    return (
                      <tr key={d.qty}>
                        <td>
                          <span className="cc-note">৳ {d.label}</span>
                        </td>
                        <td className="cc-num">
                          <span
                            className={`cc-pill ${
                              quantity ? "cc-pill-value" : "cc-pill-muted"
                            }`}
                          >
                            {quantity}
                          </span>
                        </td>
                        <td className="cc-num">{money(d.value)}</td>
                        <td
                          className={`cc-num cc-line-total ${
                            lineTotal ? "" : "is-zero"
                          }`}
                        >
                          {money(lineTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="cc-num">
                      Gross cash counted
                    </td>
                    <td className="cc-num">{money(gross)}</td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="cc-num">
                      Less: suspense
                    </td>
                    <td className="cc-num">{money(suspense)}</td>
                  </tr>
                  <tr className="cc-net">
                    <th colSpan="3" className="cc-num">
                      Net Total
                    </th>
                    <th className="cc-num">
                      ৳ {money(record.total_amount)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default View;
